import * as React from "react";

import { ApiError, updateProfile } from "@/api/client";
import { ComicPanel } from "@/components/comic";

import { FeatureState, useFeature } from "./useFeature";

/**
 * Whether people may reach this host through an agent, without holding one of their links.
 *
 * <p>The account-level half of reachability. It has existed in the database since the marketplace
 * shipped with no endpoint and no screen — the only way to turn it on was an <code>UPDATE</code>,
 * which is a feature that ships and cannot be used (D-89).</p>
 *
 * <p>Absent rather than disabled below Pro. A switch somebody cannot use is furniture, and one that
 * is visible but dead invites the question this whole design keeps trying to answer: why did
 * nothing happen?</p>
 *
 * <p>This is now the only switch. There was a second one on every rolling link, and it is gone
 * (D-96): a host said yes here and then had to say yes again on each link before anything happened,
 * which read as a switch that did nothing. Saying yes here opens the host's live rolling links —
 * which of them a caller is matched to is still decided by that link's own description and its
 * admission rules.</p>
 */
export function AgentReachPanel({
  enabled,
  onChange,
  /** Whether this plan includes being reachable. Injected so the panel is testable. */
  marketplace = useFeature("A2A_MARKETPLACE"),
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
  marketplace?: FeatureState;
}) {
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (marketplace !== "granted") return null;

  async function set(next: boolean) {
    setSaving(true);
    setError(null);
    try {
      const saved = await updateProfile({ a2aHostEnabled: next });
      onChange(saved.a2aHostEnabled);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 402
          ? "Being reachable by other people's agents is part of Pro."
          : "That did not save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <ComicPanel title="Being reachable" className="h-full">
      <label className="flex items-start justify-between gap-3">
        <span>
          <span className="block text-sm font-medium">
            Let people reach me through their agent
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Rolling links and their descriptions will be matched for users stated purpose in
            agentic chat booking
          </span>
        </span>
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0"
          checked={enabled}
          disabled={saving}
          onChange={(e) => void set(e.target.checked)}
        />
      </label>

      {error && (
        <p role="alert" className="mt-2 text-[11px] text-destructive-strong">
          {error}
        </p>
      )}
    </ComicPanel>
  );
}
